<template>
  <div class="youth-settings-page">
    <div class="youth-settings-card">
      <div class="page-header">
        <div class="header-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
        </div>
        <h1>青少年保护模式</h1>
        <p class="subtitle">为未成年人提供健康、安全的阅读环境，包含时长限制、宵禁时段、内容过滤与消费保护</p>
      </div>

      <div v-if="loading" class="loading-section">
        <div class="loading-spinner"></div>
        <p>加载中...</p>
      </div>

      <div v-else-if="notLoggedIn" class="login-required-section">
        <div class="header-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
        </div>
        <p class="login-required-text">青少年模式需要登录后使用。未成年人建议在监护人指导下开启：开启后将限制使用时长与夜间时段、过滤不适宜内容、并禁止充值消费。</p>
        <button class="save-btn" @click="goLogin">去登录 / 注册</button>
      </div>

      <template v-else>
        <!-- 模式开关 -->
        <div class="setting-section">
          <div class="setting-row">
            <div class="setting-info">
              <h3>开启青少年模式</h3>
              <p>开启后将限制使用时长、夜间宵禁、内容访问与充值购买</p>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" v-model="form.enabled" @change="onToggleChange" />
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>

        <!-- 适龄范围（分龄原则：提供年龄区间选择） -->
        <div class="setting-section" v-if="form.enabled">
          <h3 class="section-title">适龄范围</h3>
          <p class="section-desc">按《移动互联网未成年人模式建设指南》分龄推荐，选择后将优先展示适龄内容并标注适龄信息</p>
          <div class="age-group-list">
            <label v-for="opt in ageGroupOptions" :key="opt.value" class="age-group-item" :class="{ active: ageGroup === opt.value }">
              <input type="radio" :value="opt.value" v-model="ageGroup" @change="onAgeGroupChange" />
              <span class="age-group-label">{{ opt.label }}</span>
              <span class="age-group-desc">{{ opt.desc }}</span>
            </label>
          </div>
        </div>

        <!-- 设置独立密码 -->
        <div class="setting-section" v-if="form.enabled">
          <h3 class="section-title">独立密码设置</h3>
          <div v-if="!hasPassword" class="password-setup">
            <p class="section-desc">请设置4位数字密码，用于解锁、修改限制和关闭青少年模式</p>
            <p class="setup-hint">设置完成后青少年模式将正式生效。请牢记密码，修改或关闭时均需验证。</p>
            <div class="pin-input-group">
              <input v-model="newPin[0]" type="tel" maxlength="1" class="pin-input" @input="onNewPinInput(0)" ref="newPin0" />
              <input v-model="newPin[1]" type="tel" maxlength="1" class="pin-input" @input="onNewPinInput(1)" ref="newPin1" />
              <input v-model="newPin[2]" type="tel" maxlength="1" class="pin-input" @input="onNewPinInput(2)" ref="newPin2" />
              <input v-model="newPin[3]" type="tel" maxlength="1" class="pin-input" @input="onNewPinInput(3)" ref="newPin3" />
            </div>
            <p v-if="newPinError" class="error-text">{{ newPinError }}</p>
            <div v-if="newPinComplete" class="pin-confirm">
              <p class="section-desc">请再次输入密码确认</p>
              <div class="pin-input-group">
                <input v-model="confirmPin[0]" type="tel" maxlength="1" class="pin-input" @input="onConfirmPinInput(0)" ref="confirmPin0" />
                <input v-model="confirmPin[1]" type="tel" maxlength="1" class="pin-input" @input="onConfirmPinInput(1)" ref="confirmPin1" />
                <input v-model="confirmPin[2]" type="tel" maxlength="1" class="pin-input" @input="onConfirmPinInput(2)" ref="confirmPin2" />
                <input v-model="confirmPin[3]" type="tel" maxlength="1" class="pin-input" @input="onConfirmPinInput(3)" ref="confirmPin3" />
              </div>
              <p v-if="confirmPinError" class="error-text">{{ confirmPinError }}</p>
              <button class="save-btn" @click="savePassword" :disabled="savingPassword">
                {{ savingPassword ? '保存中...' : '确认设置密码' }}
              </button>
            </div>
          </div>
          <div v-else class="password-has">
            <p class="section-desc has-pwd">
              <svg class="inline-icon success" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              独立密码已设置
            </p>
            <div class="change-password-row">
              <button class="secondary-btn" @click="startChangePassword">修改密码</button>
            </div>
            <div v-if="changingPassword" class="password-setup">
              <p class="section-desc">当前密码已验证，请输入新密码（4位数字）</p>
              <div class="pin-input-group">
                <input v-model="newPin[0]" type="tel" maxlength="1" class="pin-input" @input="onNewPinInput(0)" ref="newPin0c" />
                <input v-model="newPin[1]" type="tel" maxlength="1" class="pin-input" @input="onNewPinInput(1)" ref="newPin1c" />
                <input v-model="newPin[2]" type="tel" maxlength="1" class="pin-input" @input="onNewPinInput(2)" ref="newPin2c" />
                <input v-model="newPin[3]" type="tel" maxlength="1" class="pin-input" @input="onNewPinInput(3)" ref="newPin3c" />
              </div>
              <p v-if="newPinError" class="error-text">{{ newPinError }}</p>
              <button class="save-btn" @click="savePassword" :disabled="savingPassword || !newPinComplete">
                {{ savingPassword ? '保存中...' : '确认修改' }}
              </button>
              <button class="cancel-btn mt-8" @click="cancelChangePassword">取消修改</button>
            </div>
          </div>
        </div>

        <!-- 时长和宵禁设置 -->
        <div class="setting-section" v-if="form.enabled && hasPassword">
          <div class="section-header-row">
            <h3 class="section-title">使用限制</h3>
            <button v-if="!limitEditing" class="edit-limit-btn" @click="startEditLimit">修改限制</button>
            <div v-else class="limit-edit-actions">
              <button class="cancel-btn-small" @click="cancelLimitEdit">取消</button>
              <button class="save-btn-small" @click="saveLimitEdit">保存</button>
            </div>
          </div>

          <div class="setting-row">
            <div class="setting-info">
              <h3>每日时长限制</h3>
              <p>{{ form.daily_time_limit }} 分钟</p>
            </div>
            <input type="range" :value="form.daily_time_limit" @input="onLimitInput('daily_time_limit', $event)" min="15" max="480" step="15" class="range-slider" :class="{'slider-locked': !limitEditing}" :disabled="!limitEditing" />
          </div>

          <div class="setting-row">
            <div class="setting-info">
              <h3>宵禁开始</h3>
              <p>{{ form.curfew_start }}:00</p>
            </div>
            <input type="range" :value="form.curfew_start" @input="onLimitInput('curfew_start', $event)" min="0" max="23" step="1" class="range-slider" :class="{'slider-locked': !limitEditing}" :disabled="!limitEditing" />
          </div>

          <div class="setting-row">
            <div class="setting-info">
              <h3>宵禁结束</h3>
              <p>{{ form.curfew_end }}:00</p>
            </div>
            <input type="range" :value="form.curfew_end" @input="onLimitInput('curfew_end', $event)" min="0" max="23" step="1" class="range-slider" :class="{'slider-locked': !limitEditing}" :disabled="!limitEditing" />
          </div>

          <div class="curfew-preview">
            <p v-if="form.curfew_start > form.curfew_end">
              <svg class="inline-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              宵禁时段：{{ form.curfew_start }}:00 ~ {{ form.curfew_end }}:00（次日）
            </p>
            <p v-else>
              <svg class="inline-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              宵禁时段：{{ form.curfew_start }}:00 ~ {{ form.curfew_end }}:00
            </p>
          </div>
          <p v-if="limitSaveError" class="error-text">{{ limitSaveError }}</p>
          <p v-if="!limitEditing" class="lock-hint">
            <svg class="inline-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            修改限制需要验证独立密码
          </p>
        </div>

        <!-- 修改限制密码验证弹窗 -->
        <div v-if="showLimitPinModal" class="modal-overlay" @click="showLimitPinModal = false">
          <div class="modal-content" @click.stop>
            <h3>验证密码以修改限制</h3>
            <p>请输入4位数字独立密码</p>
            <div class="pin-input-group">
              <input v-model="limitPin[0]" type="tel" maxlength="1" class="pin-input" @input="onLimitPinInput(0)" ref="limitPin0" />
              <input v-model="limitPin[1]" type="tel" maxlength="1" class="pin-input" @input="onLimitPinInput(1)" ref="limitPin1" />
              <input v-model="limitPin[2]" type="tel" maxlength="1" class="pin-input" @input="onLimitPinInput(2)" ref="limitPin2" />
              <input v-model="limitPin[3]" type="tel" maxlength="1" class="pin-input" @input="onLimitPinInput(3)" ref="limitPin3" />
            </div>
            <p v-if="limitPinError" class="error-text">{{ limitPinError }}</p>
            <div class="modal-actions">
              <button class="cancel-btn" @click="showLimitPinModal = false">取消</button>
              <button class="save-btn" :disabled="!limitPinComplete" @click="confirmLimitPin">验证</button>
            </div>
          </div>
        </div>

        <!-- 使用概况 -->
        <div class="setting-section" v-if="form.enabled">
          <h3 class="section-title">今日使用概况</h3>
          <div class="usage-bar">
            <div class="usage-bar-fill" :style="{ width: usagePercent + '%' }"></div>
          </div>
          <p class="usage-text">
            已使用 {{ usageMinutes }} / {{ form.daily_time_limit }} 分钟
            <span v-if="usagePercent >= 100" class="usage-exceeded">（已超限）</span>
          </p>
        </div>

        <!-- 关闭按钮 -->
        <div class="setting-section danger-section" v-if="form.enabled && hasPassword">
          <h3 class="section-title danger-title">关闭青少年模式</h3>
          <p class="section-desc">关闭前需要验证独立密码</p>
          <button class="danger-btn" @click="showCloseModal = true">关闭青少年模式</button>
        </div>

        <!-- 开启模式密码验证弹窗 -->
        <div v-if="showOpenModal" class="modal-overlay" @click="cancelOpen">
          <div class="modal-content" @click.stop>
            <h3>验证密码以开启</h3>
            <p>请输入4位数字独立密码</p>
            <div class="pin-input-group">
              <input v-model="openPin[0]" type="tel" maxlength="1" class="pin-input" @input="onOpenPinInput(0)" ref="openPin0" />
              <input v-model="openPin[1]" type="tel" maxlength="1" class="pin-input" @input="onOpenPinInput(1)" ref="openPin1" />
              <input v-model="openPin[2]" type="tel" maxlength="1" class="pin-input" @input="onOpenPinInput(2)" ref="openPin2" />
              <input v-model="openPin[3]" type="tel" maxlength="1" class="pin-input" @input="onOpenPinInput(3)" ref="openPin3" />
            </div>
            <p v-if="openPinError" class="error-text">{{ openPinError }}</p>
            <div class="modal-actions">
              <button class="cancel-btn" @click="cancelOpen">取消</button>
              <button class="save-btn" :disabled="!openPinComplete || opening" @click="confirmOpen">
                {{ opening ? '验证中...' : '确认开启' }}
              </button>
            </div>
          </div>
        </div>

        <!-- 修改密码：先验证当前密码 -->
        <div v-if="showOldPinModal" class="modal-overlay" @click="cancelChangePassword">
          <div class="modal-content" @click.stop>
            <h3>验证当前密码</h3>
            <p>修改密码前，请先输入当前的4位数字密码</p>
            <div class="pin-input-group">
              <input v-model="oldPin[0]" type="tel" maxlength="1" class="pin-input" @input="onOldPinInput(0)" ref="oldPin0" />
              <input v-model="oldPin[1]" type="tel" maxlength="1" class="pin-input" @input="onOldPinInput(1)" ref="oldPin1" />
              <input v-model="oldPin[2]" type="tel" maxlength="1" class="pin-input" @input="onOldPinInput(2)" ref="oldPin2" />
              <input v-model="oldPin[3]" type="tel" maxlength="1" class="pin-input" @input="onOldPinInput(3)" ref="oldPin3" />
            </div>
            <p v-if="oldPinError" class="error-text">{{ oldPinError }}</p>
            <div class="modal-actions">
              <button class="cancel-btn" @click="cancelChangePassword">取消</button>
              <button class="save-btn" :disabled="!oldPinComplete || verifyingOld" @click="confirmOldPin">
                {{ verifyingOld ? '验证中...' : '验证' }}
              </button>
            </div>
          </div>
        </div>

        <!-- 关闭确认弹窗 -->
        <div v-if="showCloseModal" class="modal-overlay" @click="cancelClose">
          <div class="modal-content" @click.stop>
            <h3>验证密码以关闭</h3>
            <p>请输入4位数字独立密码</p>
            <div class="pin-input-group">
              <input v-model="closePin[0]" type="tel" maxlength="1" class="pin-input" @input="onClosePinInput(0)" ref="closePin0" />
              <input v-model="closePin[1]" type="tel" maxlength="1" class="pin-input" @input="onClosePinInput(1)" ref="closePin1" />
              <input v-model="closePin[2]" type="tel" maxlength="1" class="pin-input" @input="onClosePinInput(2)" ref="closePin2" />
              <input v-model="closePin[3]" type="tel" maxlength="1" class="pin-input" @input="onClosePinInput(3)" ref="closePin3" />
            </div>
            <p v-if="closePinError" class="error-text">{{ closePinError }}</p>
            <div class="modal-actions">
              <button class="cancel-btn" @click="cancelClose">取消</button>
              <button class="save-btn" :disabled="!closePinComplete" @click="confirmClose">确认关闭</button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  getYouthConfig,
  setYouthConfig,
  setYouthPassword,
  verifyYouthPassword,
  getYouthUsage,
} from '@/api/index';

export default {
  name: 'YouthModePage',
  data() {
    return {
      loading: true,
      notLoggedIn: false,
      ageGroup: '12-16',
      ageGroupOptions: [
        { value: '8-12', label: '8-12周岁', desc: '通识科普与正向引导内容' },
        { value: '12-16', label: '12-16周岁', desc: '知识科普与健康娱乐内容' },
        { value: '16-18', label: '16-18周岁', desc: '适合本年龄段认知的健康内容' },
      ],
      hasPassword: false,
      form: {
        enabled: false,
        daily_time_limit: 60,
        curfew_start: 22,
        curfew_end: 6,
      },
      usageMinutes: 0,
      newPin: ['', '', '', ''],
      confirmPin: ['', '', '', ''],
      closePin: ['', '', '', ''],
      newPinError: '',
      confirmPinError: '',
      closePinError: '',
      savingPassword: false,
      showCloseModal: false,
      changingPassword: false,
      limitEditing: false,
      limitSnapshot: {},
      showLimitPinModal: false,
      limitPin: ['', '', '', ''],
      limitPinError: '',
      limitSaveError: '',
      limitVerifiedPin: '',
      showOpenModal: false,
      openPin: ['', '', '', ''],
      openPinError: '',
      opening: false,
      showOldPinModal: false,
      oldPin: ['', '', '', ''],
      oldPinError: '',
      verifyingOld: false,
      verifiedOldPin: '',
      _wasEnabled: false,
    }
  },
  computed: {
    newPinComplete() {
      return this.newPin.every(d => d !== '');
    },
    closePinComplete() {
      return this.closePin.every(d => d !== '');
    },
    limitPinComplete() {
      return this.limitPin.every(d => d !== '');
    },
    openPinComplete() {
      return this.openPin.every(d => d !== '');
    },
    oldPinComplete() {
      return this.oldPin.every(d => d !== '');
    },
    usagePercent() {
      if (!this.form.daily_time_limit) return 0;
      return Math.min(100, Math.round((this.usageMinutes / this.form.daily_time_limit) * 100));
    }
  },
  created() {
    const saved = localStorage.getItem('youthAgeGroup');
    if (saved && this.ageGroupOptions.some(o => o.value === saved)) {
      this.ageGroup = saved;
    }
    this.loadConfig();
  },
  methods: {
    async loadConfig() {
      this.loading = true;
      try {
        const token = localStorage.getItem('userToken');
        if (!token) {
          this.notLoggedIn = true;
          return;
        }
        this.notLoggedIn = false;

        const res = await getYouthConfig();
        if (res.data && res.data.code === 1) {
          const data = res.data.data;
          this.form.enabled = data.enabled;
          this.hasPassword = data.has_password;
          this.form.daily_time_limit = data.daily_time_limit;
          this.form.curfew_start = data.curfew_start;
          this.form.curfew_end = data.curfew_end;
          this.usageMinutes = data.usage_minutes;
          this._wasEnabled = !!data.enabled;
        }

        const usageRes = await getYouthUsage();
        if (usageRes.data && usageRes.data.code === 1) {
          this.usageMinutes = usageRes.data.data.usage_minutes;
        }
      } catch (e) {
        console.error('加载配置失败', e);
      } finally {
        this.loading = false;
      }
    },

    goLogin() {
      this.$router.push('/profile');
    },

    onAgeGroupChange() {
      localStorage.setItem('youthAgeGroup', this.ageGroup);
    },

    // password: 需要校验的独立密码（开启/关闭/改限制时必传，后端强制校验）
    async saveConfig(password) {
      const payload = {
        enabled: this.form.enabled ? 1 : 0,
        daily_time_limit: this.form.daily_time_limit,
        curfew_start: this.form.curfew_start,
        curfew_end: this.form.curfew_end,
      };
      if (password) payload.password = password;

      const res = await setYouthConfig(payload);
      if (!res.data || res.data.code !== 1) {
        throw new Error((res.data && res.data.msg) || '保存失败');
      }
      return res;
    },

    onToggleChange() {
      // 关闭时：开关先弹回开启态，由关闭弹窗验证密码后才真正关闭
      if (!this.form.enabled) {
        this.form.enabled = true;
        this.closePin = ['', '', '', ''];
        this.closePinError = '';
        this.showCloseModal = true;
        this.$nextTick(() => {
          if (this.$refs.closePin0) this.$refs.closePin0.focus();
        });
        return;
      }

      // 开启时：
      // - 从未设置过密码 -> 走首次设置密码流程，由 savePassword 提交 enabled=1
      if (!this.hasPassword) {
        this.newPin = ['', '', '', ''];
        this.confirmPin = ['', '', '', ''];
        this.newPinError = '';
        this.confirmPinError = '';
        return;
      }

      // - 已设置过密码 -> 必须先验证密码才能重新开启
      this.openPin = ['', '', '', ''];
      this.openPinError = '';
      this.showOpenModal = true;
      this.$nextTick(() => {
        if (this.$refs.openPin0) this.$refs.openPin0.focus();
      });
    },

    onOpenPinInput(index) {
      if (this.openPin[index] && index < 3) {
        const key = 'openPin' + (index + 1);
        this.$nextTick(() => {
          if (this.$refs[key]) this.$refs[key].focus();
        });
      }
      this.openPinError = '';
      if (this.openPinComplete) {
        this.confirmOpen();
      }
    },

    async confirmOpen() {
      const password = this.openPin.join('');
      if (!/^\d{4}$/.test(password)) {
        this.openPinError = '密码格式不正确';
        return;
      }

      this.opening = true;
      this.openPinError = '';
      try {
        await this.saveConfig(password);
        this.showOpenModal = false;
        this.openPin = ['', '', '', ''];
        this.loadConfig();
        alert('青少年模式已开启');
      } catch (e) {
        this.openPinError = e.message || '密码错误';
        this.openPin = ['', '', '', ''];
        this.$nextTick(() => {
          if (this.$refs.openPin0) this.$refs.openPin0.focus();
        });
      } finally {
        this.opening = false;
      }
    },

    cancelOpen() {
      this.showOpenModal = false;
      this.openPin = ['', '', '', ''];
      this.openPinError = '';
      this.form.enabled = false;
    },

    onLimitInput(field, e) {
      if (!this.limitEditing) {
        // 非编辑模式，强制恢复原值
        e.target.value = this.form[field];
        return;
      }
      this.form[field] = Number(e.target.value);
    },

    startEditLimit() {
      this.limitPin = ['', '', '', ''];
      this.limitPinError = '';
      this.showLimitPinModal = true;
      this.$nextTick(() => {
        if (this.$refs.limitPin0) this.$refs.limitPin0.focus();
      });
    },

    onLimitPinInput(index) {
      if (this.limitPin[index] && index < 3) {
        const key = 'limitPin' + (index + 1);
        this.$nextTick(() => {
          if (this.$refs[key]) this.$refs[key].focus();
        });
      }
      this.limitPinError = '';
      if (this.limitPinComplete) {
        this.confirmLimitPin();
      }
    },

    async confirmLimitPin() {
      const password = this.limitPin.join('');
      if (!/^\d{4}$/.test(password)) {
        this.limitPinError = '密码格式不正确';
        return;
      }
      try {
        const res = await verifyYouthPassword(password);
        if (res.data && res.data.code === 1 && res.data.data.verified) {
          this.limitSnapshot = {
            daily_time_limit: this.form.daily_time_limit,
            curfew_start: this.form.curfew_start,
            curfew_end: this.form.curfew_end,
          };
          // 保留已验证的密码，保存限制时提交给后端二次校验
          this.limitVerifiedPin = password;
          this.limitEditing = true;
          this.showLimitPinModal = false;
          this.limitPin = ['', '', '', ''];
        } else {
          this.limitPinError = '密码错误';
          this.limitPin = ['', '', '', ''];
          this.$nextTick(() => {
            if (this.$refs.limitPin0) this.$refs.limitPin0.focus();
          });
        }
      } catch (e) {
        this.limitPinError = '验证失败，请稍后重试';
      }
    },

    async saveLimitEdit() {
      // 提交限制变更时携带已验证过的密码，后端会再次校验
      this.limitSaveError = '';
      try {
        await this.saveConfig(this.limitVerifiedPin);
        this.limitEditing = false;
        this.limitVerifiedPin = '';
      } catch (e) {
        this.limitSaveError = e.message || '保存失败';
        if (/密码/.test(e.message || '')) {
          this.limitEditing = false;
          this.limitVerifiedPin = '';
          this.startEditLimit();
        }
      }
    },

    cancelLimitEdit() {
      this.form.daily_time_limit = this.limitSnapshot.daily_time_limit;
      this.form.curfew_start = this.limitSnapshot.curfew_start;
      this.form.curfew_end = this.limitSnapshot.curfew_end;
      this.limitEditing = false;
    },

    onNewPinInput(index) {
      if (this.newPin[index] && index < 3) {
        const suffix = this.changingPassword ? 'c' : '';
        const key = 'newPin' + (index + 1) + suffix;
        this.$nextTick(() => {
          if (this.$refs[key]) this.$refs[key].focus();
        });
      }
      this.newPinError = '';
    },

    onConfirmPinInput(index) {
      if (this.confirmPin[index] && index < 3) {
        const key = 'confirmPin' + (index + 1);
        this.$nextTick(() => {
          if (this.$refs[key]) this.$refs[key].focus();
        });
      }
      this.confirmPinError = '';
    },

    onClosePinInput(index) {
      if (this.closePin[index] && index < 3) {
        const key = 'closePin' + (index + 1);
        this.$nextTick(() => {
          if (this.$refs[key]) this.$refs[key].focus();
        });
      }
      this.closePinError = '';
      if (this.closePinComplete) {
        this.confirmClose();
      }
    },

    // 修改密码：先验证当前密码，验证通过后才允许输入新密码
    startChangePassword() {
      this.oldPin = ['', '', '', ''];
      this.oldPinError = '';
      this.showOldPinModal = true;
      this.$nextTick(() => {
        if (this.$refs.oldPin0) this.$refs.oldPin0.focus();
      });
    },

    onOldPinInput(index) {
      if (this.oldPin[index] && index < 3) {
        const key = 'oldPin' + (index + 1);
        this.$nextTick(() => {
          if (this.$refs[key]) this.$refs[key].focus();
        });
      }
      this.oldPinError = '';
      if (this.oldPinComplete) {
        this.confirmOldPin();
      }
    },

    async confirmOldPin() {
      const password = this.oldPin.join('');
      if (!/^\d{4}$/.test(password)) {
        this.oldPinError = '密码格式不正确';
        return;
      }

      this.verifyingOld = true;
      this.oldPinError = '';
      try {
        const res = await verifyYouthPassword(password);
        const ok = res.data && res.data.code === 1 && res.data.data.verified;
        if (!ok) {
          this.oldPinError = '当前密码错误';
          this.oldPin = ['', '', '', ''];
          this.$nextTick(() => {
            if (this.$refs.oldPin0) this.$refs.oldPin0.focus();
          });
          return;
        }
        // 验证通过，记住旧密码用于提交，并进入新密码输入
        this.verifiedOldPin = password;
        this.showOldPinModal = false;
        this.oldPin = ['', '', '', ''];
        this.changingPassword = true;
        this.newPin = ['', '', '', ''];
        this.confirmPin = ['', '', '', ''];
        this.newPinError = '';
        this.confirmPinError = '';
      } catch (e) {
        this.oldPinError = '验证失败，请稍后重试';
      } finally {
        this.verifyingOld = false;
      }
    },

    cancelChangePassword() {
      this.showOldPinModal = false;
      this.oldPin = ['', '', '', ''];
      this.oldPinError = '';
      this.verifiedOldPin = '';
      this.changingPassword = false;
      this.newPin = ['', '', '', ''];
      this.confirmPin = ['', '', '', ''];
      this.newPinError = '';
      this.confirmPinError = '';
    },

    async savePassword() {
      this.newPinError = '';
      this.confirmPinError = '';
      const password = this.newPin.join('');
      if (!/^\d{4}$/.test(password)) {
        this.newPinError = '密码必须为4位数字';
        return;
      }

      // 首次设置需要二次确认
      if (!this.changingPassword) {
        const confirm = this.confirmPin.join('');
        if (password !== confirm) {
          this.confirmPinError = '两次输入的密码不一致';
          return;
        }
      }

      // 修改密码必须携带已验证的旧密码，后端会再次校验
      if (this.changingPassword) {
        if (!this.verifiedOldPin) {
          this.newPinError = '请先验证当前密码';
          this.cancelChangePassword();
          return;
        }
        if (this.verifiedOldPin === password) {
          this.newPinError = '新密码不能与当前密码相同';
          return;
        }
      }

      this.savingPassword = true;
      try {
        const res = await setYouthPassword(
          password,
          this.changingPassword ? this.verifiedOldPin : ''
        );
        if (res.data && res.data.code === 1) {
          this.hasPassword = true;
          this.newPin = ['', '', '', ''];
          this.confirmPin = ['', '', '', ''];
          this.changingPassword = false;
          this.verifiedOldPin = '';

          // 首次设置密码（用于开启模式）时，同时提交 enabled=1
          // 首次设置场景后端不要求 password，因为此前无密码
          if (this.form.enabled && !this._wasEnabled) {
            await this.saveConfig();
          }

          alert(this.changingPassword ? '密码修改成功' : '密码设置成功');
        } else {
          this.newPinError = res.data.msg || '设置失败';
        }
      } catch (e) {
        this.newPinError = '网络错误，请稍后重试';
      } finally {
        this.savingPassword = false;
      }
    },

    cancelClose() {
      this.showCloseModal = false;
      this.closePin = ['', '', '', ''];
      this.closePinError = '';
      this.form.enabled = true; // 恢复开关为开启状态
    },

    async confirmClose() {
      const password = this.closePin.join('');
      if (!/^\d{4}$/.test(password)) {
        this.closePinError = '密码格式不正确';
        return;
      }

      try {
        const verifyRes = await verifyYouthPassword(password);
        if (!verifyRes.data || verifyRes.data.code !== 1) {
          this.closePinError = '验证失败，请稍后重试';
          this.closePin = ['', '', '', ''];
          return;
        }
        if (!verifyRes.data.data.verified) {
          this.closePinError = '密码错误';
          this.closePin = ['', '', '', ''];
          this.$nextTick(() => {
            if (this.$refs.closePin0) this.$refs.closePin0.focus();
          });
          return;
        }

        // 携带已验证的密码提交，后端强制校验后才能关闭
        await setYouthConfig({ enabled: 0, password });
        this.form.enabled = false;
        this._wasEnabled = false;
        this.showCloseModal = false;
        this.closePin = ['', '', '', ''];
        alert('青少年模式已关闭');
      } catch (e) {
        this.closePinError = (e && e.message) || '网络错误，请稍后重试';
        this.closePin = ['', '', '', ''];
      }
    },
  }
}
</script>

<style scoped>
.youth-settings-page {
  width: 50vw;
  min-width: 800px;
  max-width: none;
  min-height: 100vh;
  margin: 0 auto;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 30px;
}

.youth-settings-card {
  max-width: 580px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.header-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  color: var(--brand);
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon svg {
  width: 100%;
  height: 100%;
}

/* 行内 SVG 图标 */
.inline-icon {
  width: 14px;
  height: 14px;
  vertical-align: -2px;
  margin-right: 5px;
  flex-shrink: 0;
}

.inline-icon.success {
  color: var(--success);
}

.has-pwd {
  display: flex;
  align-items: center;
  color: var(--success);
}

.page-header h1 {
  font-size: 26px;
  color: var(--text);
  margin: 0 0 8px 0;
}

.subtitle {
  font-size: 14px;
  color: var(--text-dim);
  margin: 0;
}

.loading-section {
  text-align: center;
  padding: 60px 0;
  color: var(--text-dim);
}

.login-required-section {
  text-align: center;
  padding: 40px 10px;
}

.login-required-section .header-icon {
  color: var(--brand);
}

.login-required-text {
  font-size: 14px;
  line-height: 1.8;
  color: var(--text-dim);
  margin: 0 0 22px 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--surface-3);
  border-top: 4px solid var(--brand);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.setting-section {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
  margin: 0 0 16px 0;
}

.section-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-header-row .section-title {
  margin: 0;
}

.edit-limit-btn {
  padding: 6px 16px;
  background: var(--brand-soft);
  color: var(--brand);
  border: 1px solid var(--brand);
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-limit-btn:hover {
  background: var(--brand-soft-strong);
}

.limit-edit-actions {
  display: flex;
  gap: 8px;
}

.cancel-btn-small {
  padding: 6px 14px;
  background: var(--surface-3);
  color: var(--text-dim);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
}

.save-btn-small {
  padding: 6px 14px;
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
}

.range-slider:disabled,
.slider-locked {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.lock-hint {
  margin-top: 12px;
  font-size: 12px;
  color: var(--text-faint);
  text-align: center;
}

.section-desc {
  font-size: 13px;
  color: var(--text-dim);
  margin: 0 0 16px 0;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.setting-info h3 {
  font-size: 15px;
  color: var(--text);
  margin: 0 0 4px 0;
}

.setting-info p {
  font-size: 13px;
  color: var(--text-dim);
  margin: 0;
}

.toggle-switch {
  position: relative;
  width: 52px;
  height: 28px;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle-switch input {
  display: none;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--surface-3);
  border: 1px solid var(--border);
  border-radius: 14px;
  transition: background 0.3s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 24px;
  height: 24px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.3s;
}

.toggle-switch input:checked + .toggle-slider {
  background: var(--brand);
  border-color: var(--brand);
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(24px);
}

.pin-input-group {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 16px;
}

.pin-input {
  width: 52px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  background: var(--surface-3);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  outline: none;
}

.pin-input:focus {
  border-color: var(--brand);
}

.error-text {
  color: var(--danger);
  font-size: 14px;
  text-align: center;
  margin: 0 0 12px 0;
}

.save-btn {
  width: 100%;
  padding: 12px;
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.save-btn:hover:not(:disabled) {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.secondary-btn {
  padding: 10px 20px;
  background: var(--brand-soft);
  color: var(--brand);
  border: 1px solid var(--brand);
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.secondary-btn:hover {
  background: var(--brand-soft-strong);
}

.change-password-row {
  text-align: center;
  margin-top: 8px;
}

.range-slider {
  width: 140px;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: var(--surface-3);
  border-radius: 3px;
  outline: none;
  flex-shrink: 0;
}

.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  background: var(--brand);
  border-radius: 50%;
  cursor: pointer;
}

.curfew-preview {
  margin-top: 12px;
  padding: 10px 14px;
  background: var(--brand-soft);
  border-radius: 8px;
  font-size: 13px;
  color: var(--text-dim);
}

.usage-bar {
  height: 10px;
  background: var(--surface-3);
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 8px;
}

.usage-bar-fill {
  height: 100%;
  background: var(--brand);
  border-radius: 5px;
  transition: width 0.5s;
}

.usage-text {
  font-size: 13px;
  color: var(--text-dim);
  margin: 0;
}

.usage-exceeded {
  color: var(--danger);
  font-weight: 600;
}

.danger-section {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(248, 113, 113, 0.55);
}

.danger-section .section-desc {
  color: var(--text);
}

.danger-title {
  color: #fff;
  font-weight: 700;
}

.danger-btn {
  width: 100%;
  padding: 12px;
  background: #fff;
  color: var(--danger);
  border: 1px solid var(--danger);
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.danger-btn:hover {
  background: var(--danger);
  color: #fff;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(12, 25, 41, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: linear-gradient(180deg, #1a2f4a 0%, #0f1c2e 100%);
  border: 1px solid rgba(96, 165, 250, 0.25);
  border-radius: 16px;
  padding: 32px;
  max-width: 380px;
  width: 90%;
  text-align: center;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
}

.modal-content h3 {
  margin: 0 0 8px 0;
  color: var(--text);
}

.modal-content p {
  color: var(--text-dim);
  font-size: 14px;
  margin: 0 0 20px 0;
}

.modal-actions {
  display: flex;
  gap: 12px;
}

.modal-actions .cancel-btn,
.modal-actions .save-btn {
  flex: 1;
}

.cancel-btn {
  padding: 12px;
  background: var(--surface-3);
  color: var(--text-dim);
  border: 1px solid var(--border);
  border-radius: 10px;
  font-size: 15px;
  cursor: pointer;
}

.cancel-btn:hover {
  background: var(--brand-soft);
  border-color: var(--brand);
}

.pin-confirm {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

.password-has .section-desc {
  color: var(--success);
  font-weight: 500;
}

.password-setup {
  margin-top: 8px;
}

.mt-8 {
  margin-top: 8px;
}

/* 适龄范围选择 */
.age-group-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.age-group-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--surface-3);
  border: 1px solid var(--border);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color var(--t-fast), background var(--t-fast);
}

.age-group-item.active {
  border-color: var(--brand);
  background: var(--brand-soft);
}

.age-group-item input {
  accent-color: var(--brand);
  flex-shrink: 0;
}

.age-group-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  flex-shrink: 0;
}

.age-group-desc {
  font-size: 12px;
  color: var(--text-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.setup-hint {
  font-size: 12px;
  color: var(--text-faint);
  margin: 0 0 16px 0;
  line-height: 1.6;
}

@media (max-width: 1200px) {
  .youth-settings-page {
    width: 70vw;
    min-width: 600px;
  }
}

@media (max-width: 768px) {
  .youth-settings-page {
    width: 95vw;
    min-width: auto;
    padding: 20px;
  }

  .range-slider {
    width: 100px;
  }

  .setting-row {
    flex-wrap: wrap;
    gap: 12px;
  }
}
</style>